import { useEffect, useMemo } from "react";
import { Button, Card, Space, Table, Tag, Typography } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useRefunds from "hooks/finance/useRefunds";

const formatCurrency = (amount, currency = "USD") => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "-";

  const currencyCode = (currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch {
    return `${numericAmount.toFixed(2)} ${currencyCode}`;
  }
};

const formatDateTime = (value) =>
  value ? dayjs(value).format("YYYY-MM-DD hh:mm A") : "-";

const statusColors = {
  SUCCEEDED: { bg: "#e6fffb", color: "#006d75", border: "#87e8de" },
  PENDING: { bg: "#fffbe6", color: "#ad8b00", border: "#ffe58f" },
  FAILED: { bg: "#fff2f0", color: "#cf1322", border: "#ffa39e" },
};

const getTagStyle = (status) => {
  const colors = statusColors[String(status || "").toUpperCase()] || null;
  return {
    backgroundColor: colors?.bg || "#f5f5f5",
    color: colors?.color || "#262626",
    padding: "2px 10px",
    borderRadius: "6px",
    fontSize: "0.65rem",
    border: `1px solid ${colors?.border || "#d9d9d9"}`,
    fontWeight: 600,
    cursor: "default",
  };
};

export default function RefundHistoryPage() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { summary, loadingSummary, fetchRefundSummary, toMoney } = useRefunds();

  useEffect(() => {
    if (!transactionId) return;
    fetchRefundSummary(transactionId);
  }, [transactionId, fetchRefundSummary]);

  const computedSummary = useMemo(() => {
    if (!summary) return null;
    return {
      ...summary,
      originalAmountMoney: toMoney(summary.originalAmount),
      refundedAmountMoney: toMoney(summary.refundedAmount),
      refundableRemainingAmountMoney: toMoney(summary.refundableRemainingAmount),
    };
  }, [summary, toMoney]);

  const currency = computedSummary?.currency || "USD";
  const refunds = computedSummary?.refunds || [];

  const refundColumns = [
    {
      title: "Refund Ref",
      dataIndex: "providerRefundId",
      key: "providerRefundId",
      width: 220,
      render: (value, record) => value || record?.refundId || "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      align: "right",
      render: (amount) => (
        <Typography.Text style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatCurrency(toMoney(amount), currency)}
        </Typography.Text>
      ),
    },
    {
      title: "Type",
      dataIndex: "refundType",
      key: "refundType",
      width: 120,
      render: (value) => String(value || "-").toUpperCase(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => (
        <Tag style={getTagStyle(status)}>{String(status || "-")}</Tag>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
      responsive: ["lg"],
      render: (value) => value || "-",
    },
    {
      title: "Requested By",
      dataIndex: "requestedBy",
      key: "requestedBy",
      width: 190,
      responsive: ["lg"],
      render: (value) => value || "-",
    },
    {
      title: "Requested At",
      dataIndex: "requestedAt",
      key: "requestedAt",
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Processed At",
      dataIndex: "processedAt",
      key: "processedAt",
      width: 170,
      render: (value) => formatDateTime(value),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="file-invoice-dollar"
          title="Refund History"
          subtitle={`Transaction: ${transactionId || "-"}`}
          className="mb-2 mt-2 flex-1"
        />
        <Space>
          <Button onClick={() => navigate(-1)} icon={<ArrowLeftOutlined />}>
            Back
          </Button>
          <Button onClick={() => fetchRefundSummary(transactionId)} loading={loadingSummary} disabled={!transactionId}>
            Refresh
          </Button>
        </Space>
      </div>

      {computedSummary ? (
        <Card style={{ marginBottom: 12, borderRadius: 10 }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div style={{ minWidth: 220 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Original Amount
              </Typography.Text>
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                {formatCurrency(computedSummary.originalAmountMoney, currency)}
              </div>
            </div>
            <div style={{ minWidth: 220 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Refunded
              </Typography.Text>
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                {formatCurrency(computedSummary.refundedAmountMoney, currency)}
              </div>
            </div>
            <div style={{ minWidth: 220 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Refundable Remaining
              </Typography.Text>
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                {formatCurrency(computedSummary.refundableRemainingAmountMoney, currency)}
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      <Table
        rowKey={(record) => record.refundId || record.providerRefundId}
        columns={refundColumns}
        dataSource={refunds}
        loading={loadingSummary}
        className="custom-table"
        bordered
        size="small"
        scroll={{ x: 1200 }}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ paddingLeft: 50 }}>
              <p className="mb-1 pb-0">
                <strong>Refund ID:</strong> {record?.refundId || "-"}
              </p>
              <p className="mb-1 pb-0">
                <strong>Provider Refund ID:</strong> {record?.providerRefundId || "-"}
              </p>
              <p className="mb-1 pb-0">
                <strong>Refund Type:</strong> {String(record?.refundType || "-").toUpperCase()}
              </p>
              <p className="mb-1 pb-0">
                <strong>Status:</strong> {String(record?.status || "-")}
              </p>
              <p className="mb-1 pb-0">
                <strong>Amount:</strong> {formatCurrency(toMoney(record?.amount), currency)}
              </p>
              <p className="mb-1 pb-0">
                <strong>Reason:</strong> {record?.reason || "-"}
              </p>
              <p className="mb-1 pb-0">
                <strong>Requested By:</strong> {record?.requestedBy || "-"}
              </p>
              <p className="mb-1 pb-0">
                <strong>Requested At:</strong> {formatDateTime(record?.requestedAt)}
              </p>
              <p className="mb-1 pb-0">
                <strong>Processed At:</strong> {formatDateTime(record?.processedAt)}
              </p>
            </div>
          ),
          rowExpandable: () => true,
        }}
        locale={{ emptyText: "No refunds found for this transaction." }}
      />
    </div>
  );
}
